"use client";

import React, { useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/shadcn-components/ui/tabs";
import { Button } from "@/shadcn-components/ui/button";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/shadcn-components/ui/dropdown-menu";
import { Badge } from "@/shadcn-components/ui/badge";
import { Download } from "lucide-react";
import { TabType } from "../HistoryScreen";
import {
  useSortStore,
  PositionSortField,
  OrderSortField,
  DealSortField,
  SORT_FIELD_LABELS,
} from "@/vybe-trader-client/trade-history/stores/useSortStore";

interface HeaderTabsProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  onClockClick: () => void;
  onSortChange?: (sort: any) => void;
  /** Export currently filtered rows for the active tab to CSV */
  onExportCsv?: () => void;
}

export default function HeaderTabs({
  activeTab,
  onTabChange,
  onClockClick,
  onSortChange,
  onExportCsv,
}: HeaderTabsProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const {
    positionsSort,
    setPositionsSort,
    ordersSort,
    setOrdersSort,
    dealsSort,
    setDealsSort,
  } = useSortStore();

  const getSortOptions = () => {
    switch (activeTab) {
      case "positions":
        return Object.entries(SORT_FIELD_LABELS.positions);
      case "orders":
        return Object.entries(SORT_FIELD_LABELS.orders);
      case "deals":
        return Object.entries(SORT_FIELD_LABELS.deals);
      default:
        return [];
    }
  };

  const getCurrentSort = () => {
    switch (activeTab) {
      case "positions":
        return positionsSort;
      case "orders":
        return ordersSort;
      case "deals":
        return dealsSort;
      default:
        return null;
    }
  };

  const handleSortSelect = (field: string) => {
    switch (activeTab) {
      case "positions":
        setPositionsSort(field as PositionSortField);
        break;
      case "orders":
        setOrdersSort(field as OrderSortField);
        break;
      case "deals":
        setDealsSort(field as DealSortField);
        break;
    }
  };

  const sortOptions = getSortOptions();
  const currentSort = getCurrentSort();

  return (
    <div className="flex items-center justify-between px-3 h-11 bg-background border-b border-border">
      {/* Sort Dropdown */}
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 ml-1"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
            </svg>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="start" 
          className="w-56"
        >
          <DropdownMenuLabel className="text-sm font-medium">
            Sort {activeTab}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {sortOptions.map(([field, label]) => {
            const isSelected = currentSort?.field === field;
            const isDefault = field === "default";
            
            return (
              <DropdownMenuItem
                key={field}
                onSelect={(e) => {
                  e.preventDefault();
                  handleSortSelect(field);
                }}
                className="flex items-center justify-between cursor-pointer px-3 py-2.5"
              >
                <span className="text-sm font-medium">{label}</span>
                
                <div className="flex items-center gap-2">
                  {isSelected && !isDefault && (
                    <Badge variant="outline" className="text-xs h-5 px-1.5">
                      {currentSort?.direction === "asc" ? "↑" : "↓"}
                    </Badge>
                  )}
                  {isSelected && isDefault && (
                    <Badge variant="default" className="text-xs h-5 px-1.5">
                      ✓
                    </Badge>
                  )}
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => onTabChange(v as TabType)}
        className="flex-1 max-w-md mx-3"
      >
        <TabsList className="grid w-full grid-cols-3 h-8">
          <TabsTrigger value="positions" className="text-xs py-1">
            Positions
          </TabsTrigger>
          <TabsTrigger value="orders" className="text-xs py-1">
            Orders
          </TabsTrigger>
          <TabsTrigger value="deals" className="text-xs py-1">
            Deals
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex items-center gap-0.5 mr-1 shrink-0">
        {onExportCsv && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            title={`Export ${activeTab} to CSV`}
            onClick={onExportCsv}
          >
            <Download className="h-4 w-4 text-muted-foreground" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={onClockClick}
          className="h-9 w-9"
          title="Filters"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </Button>
      </div>
    </div>
  );
}